import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import ProgressWizard from 'components/field/ProgressWizard';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { AUTOCOMPLETE, SHIPPING_PRICES } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faAngleLeft, faAngleRight, faBookOpen, faKey } from '@fortawesome/pro-solid-svg-icons';

import * as S from './CreateInvoiceForm.styled';

/**
 * @author @muhammedburakak
 */

const InvoiceFilters = ({ isLoading, watch, setValue }) => {
	const defaultColSpan = {
		md: 6
	};

	const invoiceClientAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);
	const invoiceDeliveryCompanyAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	return (
		<>
			<FormItem colSpan={defaultColSpan} label="client" isLoading={isLoading} required>
				<AutoComplete
					name="clientCompanyID"
					autoCompleteName={AUTOCOMPLETE.CLIENTS_FOR_INVOICE}
					columns={invoiceClientAutocompleteColumns}
					selectionLabelAccessor={['Name']}
					value={watch(`clientCompanyID`)}
					setValue={(value) => {
						setValue('clientCompanyID', value?.id ?? null, { shouldValidate: true, shouldDirty: true });
					}}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="invoicePeriod" isLoading={isLoading} required>
				<EnumSelect
					searchable={false}
					name="invoicePeriod"
					enumName="PriceConfigurationInvoicePeriod"
					setValue={(value) => {
						setValue(`invoicePeriod`, value, { shouldValidate: true, shouldDirty: true });
					}}
					value={watch(`invoicePeriod`)}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="deliveryCompany" isLoading={isLoading}>
				<AutoComplete
					name="deliveryCompanyID"
					autoCompleteName={AUTOCOMPLETE.DELIVERY_COMPANY_NAME_FOR_INVOICE}
					columns={invoiceDeliveryCompanyAutocompleteColumns}
					selectionLabelAccessor={['Name']}
					value={watch(`deliveryCompanyID`)}
					setValue={(value) => {
						setValue('deliveryCompanyID', value?.id ?? null, {
							shouldValidate: true,
							shouldDirty: true
						});
					}}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="serviceType" isLoading={isLoading}>
				<EnumSelect
					searchable={false}
					name="serviceType"
					enumName="PriceConfigurationServiceType"
					setValue={(value) => {
						setValue(`serviceType`, value, { shouldValidate: true, shouldDirty: true });
					}}
					value={watch(`serviceType`)}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="shipmentChargingDate" isLoading={isLoading}>
				<DateInput
					name="shipmentChargingDate"
					rangeMode
					value={watch('shipmentChargingDate')}
					setValue={(value) => {
						setValue('shipmentChargingDate', value ?? null, { shouldValidate: true, shouldDirty: true });
					}}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="chargingStatus" isLoading={isLoading}>
				<EnumSelect
					searchable={false}
					name="shipmentChargingStatus"
					enumName="PriceConfigurationChargingStatus"
					setValue={(value) => {
						setValue(`shipmentChargingStatus`, value, { shouldValidate: true, shouldDirty: true });
					}}
					value={watch(`shipmentChargingStatus`)}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="shipmentCreationDate" isLoading={isLoading}>
				<DateInput
					name="shipmentCreationDate"
					rangeMode
					value={watch('shipmentCreationDate')}
					setValue={(value) => {
						setValue('shipmentCreationDate', value, { shouldValidate: true, shouldDirty: true });
					}}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="recordDate" isLoading={isLoading}>
				<DateInput
					name="recordDate"
					rangeMode
					value={watch('recordDate')}
					setValue={(value) => {
						setValue('recordDate', value, { shouldValidate: true, shouldDirty: true });
					}}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="deliveryDate" isLoading={isLoading}>
				<DateInput
					name="deliveryDate"
					rangeMode
					value={watch('deliveryDate')}
					setValue={(value) => {
						setValue('deliveryDate', value, { shouldValidate: true, shouldDirty: true });
					}}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="recordType" isLoading={isLoading}>
				<EnumSelect
					searchable={false}
					name="allRecordType"
					enumName="recordType"
					setValue={(value) => {
						setValue(`recordType`, value, { shouldValidate: true, shouldDirty: true });
					}}
					value={watch(`recordType`)}
				/>
			</FormItem>
		</>
	);
};

const InvoiceSummary = ({ watch }) => (
	<S.ItemContainer className="mt-4">
		<S.ItemBox>
			<div style={{ display: 'flex', justifyContent: 'space-between' }}>
				<Typography className="mb-4">excludedRecords</Typography>

				<Typography fontWeight="500">{watch('excludedRecords') ?? '-'}</Typography>
			</div>

			<div style={{ display: 'flex', justifyContent: 'space-between' }}>
				<Typography>totalPrice</Typography>

				<Typography fontWeight="500">{watch('excludedTotalPrice') ?? '-'}</Typography>
			</div>
		</S.ItemBox>

		<S.ItemBox>
			<div style={{ display: 'flex', justifyContent: 'space-between' }}>
				<Typography className="mb-4">includedRecords</Typography>

				<Typography fontWeight="500">{watch('includedRecords') ?? '-'}</Typography>
			</div>

			<div style={{ display: 'flex', justifyContent: 'space-between' }}>
				<Typography>totalPrice</Typography>

				<Typography fontWeight="500">{watch('includedTotalPrice') ?? '-'}</Typography>
			</div>
		</S.ItemBox>
	</S.ItemContainer>
);

const ProgressWizardTabs = ({ tab, isLoading, watch, setValue }) => {
	const progressWizardTabs = (tab) => {
		switch (tab) {
			case 0:
				return <InvoiceFilters isLoading={isLoading} watch={watch} setValue={setValue} />;
			case 1:
				return <InvoiceSummary isLoading={isLoading} watch={watch} />;
			default:
				return null;
		}
	};

	return progressWizardTabs(tab);
};

const CreateInvoiceForm = NiceModal.create(() => {
	const modal = useModal();
	const dispatch = useDispatch();
	const axios = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();

	const stages = useMemo(
		() => [
			{ name: 'invoiceFilters', icon: faBookOpen },
			{ name: 'invoiceSummary', icon: faKey }
		],
		[]
	);

	const [isLoading, setIsLoading] = useState(false);
	const [tab, setTab] = useState(0);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					clientCompanyID: yup.number().nullable().required(),
					invoicePeriod: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			clientCompanyID: null,
			invoicePeriod: null,
			deliveryCompanyID: null,
			serviceType: null,
			shipmentChargingDate: null,
			chargingStatus: null,
			shipmentCreationDate: null,
			recordDate: null,
			deliveryDate: null,
			recordType: null
		}
	});

	const {
		handleSubmit,
		watch,
		setValue,
		formState: { isDirty }
	} = formMethods;

	const handleCloseModal = () => {
		modal.remove();
	};

	const getInvoiceInfo = async (data) => {
		setIsLoading(true);
		axios
			.post(SHIPPING_PRICES.GET_INVOICE_INFO_TO_BE_CREATED, {
				companyID: data.clientCompanyID,
				invoicePeriod: data.invoicePeriod,
				deliveryCompanyID: data.deliveryCompanyID,
				serviceType: data.serviceType,
				shipmentChargingDate: data.shipmentChargingDate,
				shipmentChargingStatus: data.shipmentChargingStatus,
				shipmentCreationDate: data.shipmentCreationDate,
				recordDate: data.recordDate,
				deliveryDate: data.deliveryDate,
				recordType: data.recordType
			})
			.then((response) => {
				if (response.status === 200) {
					nextStep();

					setValue('excludedRecords', response.data.excludedRecords);
					setValue('excludedTotalPrice', response.data.excludedTotalPrice);
					setValue('includedRecords', response.data.includedRecords);
					setValue('includedTotalPrice', response.data.includedTotalPrice);
				} else {
					showError();
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				showError();
				AppLogger.error(error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const onSubmit = async (data) => {
		setIsLoading(true);
		axios
			.post(SHIPPING_PRICES.CREATE_INVOICE, {
				companyID: data.clientCompanyID,
				invoicePeriod: data.invoicePeriod,
				deliveryCompanyID: data.deliveryCompanyID,
				serviceType: data.serviceType,
				shipmentChargingDate: data.shipmentChargingDate,
				shipmentChargingStatus: data.shipmentChargingStatus,
				shipmentCreationDate: data.shipmentCreationDate,
				recordDate: data.recordDate,
				deliveryDate: data.deliveryDate,
				recordType: data.recordType
			})
			.then((response) => {
				if (response.status === 200) {
					showSuccess();
					nextStep();
					handleCloseModal();
					dispatch(updateDynamicListData(DYNAMICLISTS.INVOICE_LIST));
				} else {
					showError();
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				showError();
				AppLogger.error(error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const nextStep = () => {
		if (tab < 3 - 1) {
			setTab(tab + 1);
		}
	};

	const prevStep = () => {
		if (tab > 0) {
			setTab(tab - 1);
		}
	};

	return (
		<Modal fullwidth maxWidth="medium" className="modal-createInvoiceForm">
			<ModalTitle>
				<Typography variant="p1">createInvoice</Typography>
			</ModalTitle>

			<ModalContent>
				<ProgressWizard stages={stages} setIndex={setTab} index={tab} gutterBottom />

				<Form formMethods={formMethods} borderless>
					<ProgressWizardTabs tab={tab} isLoading={isLoading} watch={watch} setValue={setValue} />
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				{tab !== 0 && (
					<Button
						label="previous"
						variant="outline"
						icon={faAngleLeft}
						onClick={() => prevStep()}
						loading={isLoading}
					/>
				)}
				{tab !== 1 && (
					<Button
						icon={tab !== 2 && faAngleRight}
						label="continue"
						iconPos="right"
						onClick={handleSubmit(getInvoiceInfo, (errors) => {
							AppLogger.log(errors);
						})}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						variant="blue"
						loading={isLoading}
					/>
				)}
				{tab === 1 && (
					<Button
						className="saveButton"
						label="createInvoice"
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						variant="blue"
						loading={isLoading}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default CreateInvoiceForm;
